import React, {createRef, useState, useContext} from 'react';
import { DragDropContext, Droppable, Draggable } from '@react-forked/dnd';
import axios from "axios";
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import __ from "lodash";
import MaterialTable from "@material-table/core";
import {formatChars} from "../../../blocks/FormatCharacters";
import {defaultComponentsScroll, MaterialTableBody, MaterialTableBodyRow} from "../../../controls/Material-Table-Components"
import {TablePagination, Button, Chip, Checkbox, useTheme} from "@material-ui/core";
import {CheckboxEditor, ComboSelect, MaskedText} from "../../../controls";
import SystemContext from "../../../SystemContext";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import CostCategoriesListValidator from "../validators/CostCategoriesListValidator";
import CostCategoryLinesList from "./CostCategoryLinesList";
import { useCostCategories, useAddCostCategory, useUpdateCostCategory, useDeleteCostCategory } from "../../../services/costings";
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import useDimensions from '../../../blocks/useDimensions';


const apiSitePrefix = getApiSitePrefix();


const CostCategoriesList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const eventBus = SystemData.eventBus;
  const validatorColumn = ValidatorColumn(CostCategoriesListValidator, true);

  const props = {
    ..._props
  }
  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null,
    costingGroup: {}
  });
  const classes = props.classes;
  if (eventBus) {
    eventBus.bind('CostCategoriesList.rowSelected', (costingGroup)=>{
      setState({...state, costingGroup: costingGroup});
    });
  }

  const costingGroup = state.costingGroup;

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddCostCategory = useAddCostCategory();
  const handleUpdateCostCategory = useUpdateCostCategory();
  const handleDeleteCostCategory = useDeleteCostCategory();

  const queryClient = useQueryClient();
  const extraKeys = [costingGroup.costingGroupUuid];

  const costCategories = useCostCategories({tableRef, queryClient, extraKeys});

  const updatePriorities = async (changedRecords) => {
    SystemData.toggleLoadingProgress(true);
    let response = null;
    const errorText = `An unexpected error has occurred re-ordering the cost categories.`;
    try {
      response = await axios.put(`${apiSitePrefix}/costcategories/reorder`, changedRecords);
    } catch (err) {
      SystemData.openAlert(
        "Error",
        errorText
      );
      console.debug(err);
    }
    if (!response) {
      SystemData.openAlert(
        "Error",
        errorText
      );
    } else if (response.error) {
      SystemData.openAlert(
        "Error",
        `Error: ${response.error.errorMessage}`
      );
    }
    queryClient.invalidateQueries(["CostCategories"]);
    SystemData.toggleLoadingProgress(false);
  }

  const onDragEnd = (dragProps) => {
    if (!dragProps) return;
    if (!dragProps.source || !dragProps.destination) return;
    if (dragProps.source.index === dragProps.source.destination) return;
    if (bodyRef.current && bodyRef.current.props && bodyRef.current.props.renderData) {
      let records = [...bodyRef.current.props.renderData];
      let sourceRecord = records.splice(dragProps.source.index,1);
      const part1 = [...records].slice(0,dragProps.destination.index);
      const part2 = [...records].slice(dragProps.destination.index);
      const newRecords = [...part1, ...sourceRecord, ...part2];
      // const associatedRecords = __.filter(newRecords, __.pick(sourceRecord[0], ['origin', 'destination', 'chargeCode']));
      const associatedRecords = newRecords;
      const changes = [];
      for (let i = 0; i < associatedRecords.length; i++) {
        const newDisplayOrder = ((i + 1) * 10);
        if (associatedRecords[i].displayOrder !== newDisplayOrder) {
          changes.push({...associatedRecords[i], displayOrder: newDisplayOrder});
        }
      }
      const key = ["CostCategories", costingGroup.costingGroupUuid];
      const queryData = queryClient.getQueryData(key);
      for (let i = 0; i < changes.length; i++) {
        const {costCategoryUuid, displayOrder} = changes[i];
        const queryLine = __.find(queryData, {costCategoryUuid});
        if (queryLine) {
          queryLine['displayOrder'] = displayOrder;
        }
      }
      const sortedQueryData = __.sortBy(queryData, ['displayOrder']);
      queryClient.setQueryData(key, sortedQueryData);

      updatePriorities(changes);
    }
  };

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { fontSize:14, padding: "8px 2px" },
      headerStyle: { padding: "8px 0px" },
      filterCellStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false,
    };

    // const defaultEditProps = {
    //   style:{fontSize: "0.875rem"}
    // };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...ColumnWidth(200, undefined,undefined,undefined, {width: 76},undefined,false),
        title: "Category Name",
        field: "costCategoryName",
        initialEditValue: "",
        editable: state.canEdit ? "always" : "never",
      },
      // {
      //   ...baseColumn,
      //   ...ColumnWidth(100, undefined,undefined,undefined, undefined,undefined,false),
      //   title: "Allow Multiple",
      //   field: "allowMultiple",
      //   editable: state.canEdit ? "always" : "never",
      //   initialEditValue: "",
      //   editComponent: (props) => (
      //     <CheckboxEditor
      //       value={props.value}
      //       checked={props.value}
      //       onChange={ (checked) => {
      //         props.onChange(checked)
      //       }}
      //     />
      //   ),
      //   render: rowData => <Checkbox style={{padding: 0}} checked={rowData.allowMultiple === true} value="true" />
      // },
      {
        ...baseColumn,
        ...ColumnWidth(85, undefined,undefined,undefined, undefined,undefined,false),
        title: "Auto Select",
        field: "autoSelect",
        editable: state.canEdit ? "always" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <CheckboxEditor
            value={props.value}
            checked={props.value}
            onChange={ (checked) => {
              props.onChange(checked)
            }}
          />
        ),
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.autoSelect === true} value="true" />
      },
      {
        ...baseColumn,
        ...ColumnWidth(130, undefined,undefined,undefined, undefined,undefined,false),
        title: "Require Description",
        field: "costDescriptionRequired",
        editable: state.canEdit ? "always" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <CheckboxEditor
            value={props.value}
            checked={props.value}
            onChange={ (checked) => {
              props.onChange(checked)
            }}
          />
        ),
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.costDescriptionRequired === true} value="true" />
      },
      {
        ...baseColumn,
        ...ColumnWidth(150, undefined,undefined,undefined, undefined,undefined,false),
        title: "Value Entry Type",
        field: "valueEntryType",
        initialEditValue: "",
        editable: state.canEdit ? "always" : "never",
        editComponent: (props) => (
          // <div style={{fontSize: fontSize, marginTop: -3}}>
            <ComboSelect
              group="COSTING UNIT TYPES"
              subGroup=""
              fullWidth
              value={props.value || ""}
              onChange={props.onChange}
            />
          // </div>
        ),
        render: (rowData) => (
          <ComboSelect
            group="COSTING UNIT TYPES"
            subGroup=""
            fullWidth
            value={rowData.valueEntryType || ""}
            displayOnly={true}
            // onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(100, undefined,undefined,undefined, undefined,undefined,false),
        title: "Ledger Code",
        field: "ledgerCode",
        initialEditValue: "",
        editable: state.canEdit ? "always" : "never",
        editComponent: (props) => (
          <MaskedText
            value={props.value}
            mask={"aaa-aaaaaaaa"}
            maskChar={""}
            alwaysShowMask={false}
            forceUpperCase={true}
            formatChars={formatChars}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        title: "Notes",
        field: "notes",
        initialEditValue: "",
        editable: state.canEdit ? "always" : "never",
      },
    ];
  };

  // eslint-disable-next-line no-unused-vars
  const [tableContainerRef, { width: tableWidth, height: tableHeight}] = useDimensions({});

  const theader = tableContainerRef.current?.querySelector('thead');
  const thead = theader ? theader.clientHeight:36;
  const bodyHeight = (tableHeight - thead) - 14 ;

  const bodyRef = createRef();

  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
      <div ref={tableContainerRef} className={state.canEdit?classes.tariffLineListEdit:classes.tariffLineList} style={{width: "100%", minWidth: "100%", height: `calc(100vh - ${props.height + 82}px)`}}>
      {!state.costingGroup || Object.keys(state.costingGroup).length === 0?(
        <div style={{position: "relative", width: "100%", top: "50%", height: "30px", textAlign: "center"}}>
          <Button color={"primary"}>Select a Costing Group to view additional details</Button>
        </div>
      ):(
        <MaterialTable
          tableRef={tableRef}
          style={{
            border: "none",
            boxShadow: "none",
            borderRadius: 0,
          }}
          options={{
            actionsColumnIndex: -1,
            cellStyle: {
              padding: "8px 2px",
            },
            headerRowStyle: {
              display: "table",
              width: "100%",
            },
            headerStyle: {
              backgroundColor: theme.palette.tableHeader.backgroundColor,
              color: theme.palette.tableHeader.textColor,
              padding: "2px",
            },
            draggable: false,
            padding: "dense",
            toolbarButtonAlignment: "right",
            pageSize: 10,
            detailPanelType: "single",
            paging: false,
          }}
          components={{
            ...defaultComponentsScroll,
            Body: (props) => (
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable
                  droppableId={"singleColumnDroppableAreaThusStaticInput"}
                >
                  {(provided) => (
                    <MaterialTableBody
                      ref={bodyRef}
                      {...props}
                      forwardedRef={provided.innerRef}
                      placeHolder={provided.placeholder}
                      rowHeight={40}
                      overscanRowCount={1000}
                      addVirtualScrolling={true}
                      bodyHeight={bodyHeight}
                      headerHeight={thead}
                      filterRowHeight={40}
                    />
                  )}
                </Droppable>
              </DragDropContext>
            ),
            Row: (props) => (
              <Draggable
                draggableId={props.data.tableData.id.toString()}
                index={props.data.tableData.id}
              >
                {(provided) => {
                  return (
                    <MaterialTableBodyRow
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      {...props}
                      forwardedRef={provided.innerRef}
                    />
                  );
                }}
              </Draggable>
            ),
            Pagination: (props) => (
              <TablePagination
                {...props}
                rowsPerPageOptions={[5, 10, 15]}
              />
            ),
          }}
          detailPanel={
            [
              {
                tooltip: "Cost Category Details",
                icon: `request_quote`,
                // openIcon: `request_quote_two_tone`,
                render: (rowData) => {
                  return (
                    <div
                      className={classes.tariffBandList}
                      style={{ backgroundColor: "#f2f2f2"}}
                    >
                      <CostCategoryLinesList
                        canEdit={state.canEdit}
                        costCategoryUuid={rowData.costCategoryUuid}
                        classes={classes}
                      />
                    </div>
                  );
                },
              },
            ]
          }
          title={`Cost Categories ${costingGroup.costingGroupName?"of "+costingGroup.costingGroupName:""}`}
          columns={getColumns({canEdit: state.canEdit})}
          data={costCategories.data}
          editable={{
            isEditable: () => {
              return state.canEdit;
            },
            isDeletable: () => {
              return state.canEdit;
            },
            onRowAdd: state.canEdit
              ? (newData) => new Promise((resolve, reject) => {
                handleAddCostCategory.mutate({record: {...newData, costingGroupUuid: costingGroup.costingGroupUuid} , resolve, reject, extraKeys});
              })
              : undefined,
            onRowUpdate: (newData, oldData) =>
              new Promise((resolve, reject) => {
                handleUpdateCostCategory.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
              }),
            onRowDelete: (oldData) =>
              new Promise((resolve, reject) => {
                handleDeleteCostCategory.mutate({oldRecord: oldData, resolve, reject, extraKeys});
              }),
          }}
        />
      )}
      </div>
    </div>
  );

}

export default CostCategoriesList;
